"use client";

import {
  ServicePageLayout,
  ConsultationCTA,
  ServiceHeroImage,
  ServiceSection,
  ServiceGrid,
  ServiceFeatureCard,
  ServiceFaq,
} from "@/components/services";
import { StructuredData } from "@/components/common";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";

const smImage = "/images/services/svc-smart-manufacturing.jpg";
const smFactoryImage = "/images/services/sm.jpg";

// Cancels the layout container padding so band sections read full-width.
const bandBleed = "-mx-4 sm:-mx-6 lg:-mx-8";

// Concrete figures drawn from the FAQ copy below — the page's proof moment.
// Figures here must be traceable. The previous "Up to 50% less unplanned
// downtime" was unsourced anywhere in the repo and could not be traced;
// replaced 2026-08-26 with the US DOE O&M Best Practices Guide figures, which
// are smaller but citable. See docs/research/predictive-maintenance.md.
const proofStats = [
  {
    value: "8–12%",
    label: "lower maintenance cost with predictive over preventive (US DOE O&M Best Practices Guide)",
  },
  {
    value: "4-8 weeks",
    label: "to deploy a focused IoT pilot",
  },
  {
    value: "Month 1",
    label: "quick-win deployments show ROI",
  },
];

const smFAQs = [
  {
    question: "What is smart manufacturing and how is it different from traditional manufacturing?",
    answer: "Smart manufacturing uses IoT sensors, AI, and real-time data analytics to create connected, automated factory environments. Unlike traditional manufacturing which relies on manual monitoring and scheduled maintenance, smart manufacturing enables predictive maintenance, real-time quality control, and data-driven decision making. On maintenance specifically, the US Department of Energy's O&M Best Practices Guide puts condition-based predictive maintenance at an estimated 8–12% cost saving over a scheduled preventive programme, and at $9 per horsepower per year against $13 for preventive and $18 for reactive."
  },
  {
    question: "How much does it cost to implement smart manufacturing in Malaysia?",
    answer: "Implementation costs depend on factory size and scope. A pilot project covering one production line typically starts from RM 50,000-150,000. Full facility-wide Industry 4.0 transformation ranges from RM 200,000 to RM 1 million+. Malaysian manufacturers can leverage government incentives like the Industry4WRD program and MIDA grants to offset costs."
  },
  {
    question: "Which industries in Malaysia benefit most from smart manufacturing?",
    answer: "Electronics and semiconductor manufacturing, automotive parts, food and beverage processing, pharmaceutical, and rubber/palm oil processing industries in Malaysia see the highest ROI. Factories in Selangor, Penang, and Johor industrial zones are rapidly adopting Industry 4.0 solutions to stay globally competitive."
  },
  {
    question: "How long does it take to implement smart manufacturing solutions?",
    answer: "A focused IoT pilot project can be deployed in 4-8 weeks. A comprehensive smart manufacturing transformation covering multiple production lines typically takes 3-9 months. Anchor Sprint uses an incremental approach — starting with quick-win IoT deployments that show ROI within the first month, then expanding across the facility."
  },
  {
    question: "Does Anchor Sprint provide both the hardware and software for smart manufacturing?",
    answer: "Yes, Anchor Sprint provides end-to-end smart manufacturing solutions including IoT sensor hardware, connectivity infrastructure, data analytics platforms, AI-powered monitoring dashboards, and integration with existing MES/ERP systems. We partner with leading industrial IoT hardware vendors and handle everything from assessment to deployment and ongoing support."
  }
];

export default function SmartManufacturing() {
  const locale = useLocale();
  const translate = (text: string) => t(locale, text);
  const translatedFaqs = smFAQs.map((faq) => ({
    question: translate(faq.question),
    answer: translate(faq.answer),
  }));

  return (
    <ServicePageLayout
      title="Smart Manufacturing Solutions in Malaysia"
      breadcrumb="Services / Smart Manufacturing"
      currentServiceId="sm"
      accent="Smart Manufacturing"
      dek="IoT, AI and real-time analytics that turn Malaysian factories into connected, predictive operations."
    >
      <ServiceHeroImage src={smImage} alt="Smart Manufacturing Solutions Malaysia" />

      <ServiceSection
        title="What is Smart Manufacturing?"
        titleLevel="h2"
        eyebrow="Industry 4.0"
        accent="Smart Manufacturing"
        dek="Smart Manufacturing represents the convergence of digital technology and traditional manufacturing processes. It integrates real-time data, IoT devices, artificial intelligence, and automation to create a more connected, efficient, and adaptive manufacturing environment."
        band="ivory"
        className={bandBleed}
      >
        <div className="overflow-hidden rounded-2xl border border-border">
          <img
            src={smFactoryImage}
            alt={translate(
              "Smart factory production line with connected IoT monitoring",
            )}
            loading="lazy"
            width={1200}
            height={500}
            decoding="async"
            className="h-[360px] w-full object-cover"
          />
        </div>

        {/* Dark proof moment — concrete figures from the FAQ copy */}
        <div className="mt-10 rounded-3xl bg-hero-background px-6 py-12 sm:px-10 lg:px-14">
          <p className="mb-10 font-mono text-xs font-semibold uppercase tracking-[0.3em] text-primary-contrast/50">
            {translate("What Malaysian factories achieve")}
          </p>
          <div className="grid gap-10 sm:grid-cols-3">
            {proofStats.map((stat) => (
              <div key={stat.value}>
                <p className="font-display text-4xl font-bold tracking-tight text-primary-contrast lg:text-5xl">
                  {translate(stat.value)}
                </p>
                <p className="mt-3 font-mono text-xs uppercase tracking-wider text-primary-contrast/60">
                  {translate(stat.label)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </ServiceSection>

      <ServiceSection
        title="Our Smart Manufacturing Services"
        titleLevel="h2"
        eyebrow="What we build"
        accent="Services"
        dek="Concrete deployments we deliver on Malaysian factory floors — scoped to start small and scale."
        band="surface"
        className={bandBleed}
      >
        <ServiceGrid columns={3}>
          <ServiceFeatureCard
            icon="fas fa-microchip"
            title="IoT Sensor Retrofits"
            description="Instrument existing machines with sensors and edge gateways — no production-line replacement required."
          />
          <ServiceFeatureCard
            icon="fas fa-network-wired"
            title="MES & ERP Integration"
            description="Connect shop-floor data to your MES and ERP so planning, inventory, and finance see real-time production truth."
          />
          <ServiceFeatureCard
            icon="fas fa-wrench"
            title="Predictive Maintenance"
            description="AI models on machine telemetry that flag failures before they stop the line."
          />
          <ServiceFeatureCard
            icon="fas fa-chart-line"
            title="Production Dashboards"
            description="Live OEE, quality, and throughput dashboards for supervisors and management."
          />
          <ServiceFeatureCard
            icon="fas fa-robot"
            title="Process Automation"
            description="Automate quality inspection and repetitive handling with vision systems and robotics integration."
          />
          <ServiceFeatureCard
            icon="fas fa-shield-alt"
            title="OT Security & Compliance"
            description="Segment and secure operational technology networks as they come online."
          />
        </ServiceGrid>
      </ServiceSection>

      <ServiceSection
        title="Why Smart Manufacturing Matters"
        titleLevel="h2"
        eyebrow="Benefits"
        accent="Matters"
        dek="Four outcomes manufacturers measure within the first quarters of deployment."
        band="ivory"
        className={bandBleed}
      >
        <ServiceGrid columns={2}>
          <ServiceFeatureCard
            icon="fas fa-chart-line"
            title="Enhanced Productivity"
            description="Achieve higher output with optimized processes, reduced downtime, and streamlined operations through real-time monitoring and predictive maintenance."
            features={[
              "Reduced downtime",
              "Optimized processes",
              "Improved efficiency",
              "Enhanced throughput",
            ]}
            variant="inline-icon"
          />

          <ServiceFeatureCard
            icon="fas fa-check-circle"
            title="Quality Assurance"
            description="Maintain consistent product quality through automated quality control, real-time monitoring, and data-driven process improvements."
            features={[
              "Reduced defects",
              "Consistent output",
              "Real-time quality control",
              "Automated inspections",
            ]}
            variant="inline-icon"
          />

          <ServiceFeatureCard
            icon="fas fa-dollar-sign"
            title="Cost Efficiency"
            description="Reduce operational costs through optimized resource utilization, minimized waste, and improved energy efficiency."
            features={[
              "Lower maintenance costs",
              "Reduced waste",
              "Energy optimization",
              "Resource efficiency",
            ]}
            variant="inline-icon"
          />

          <ServiceFeatureCard
            icon="fas fa-sync"
            title="Agile Operations"
            description="Respond quickly to market changes and customer demands with flexible, data-driven manufacturing processes."
            features={[
              "Digital transformation",
              "Data-driven decisions",
              "Competitive advantage",
              "Future-ready operations",
            ]}
            variant="inline-icon"
          />
        </ServiceGrid>
      </ServiceSection>

      {/* FAQ Section for rich snippets */}
      <ServiceSection
        title="Frequently Asked Questions"
        titleLevel="h2"
        eyebrow="FAQ"
        band="ivory"
        className={bandBleed}
      >
        <ServiceFaq items={smFAQs} />
      </ServiceSection>
      <StructuredData type="faq" faqs={translatedFaqs} />

      <ConsultationCTA
        title="Ready to Transform Your Manufacturing Operations?"
        description="Let's discuss how our smart manufacturing solutions can help your facility achieve greater efficiency and competitiveness."
      />
    </ServicePageLayout>
  );
}
